import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Typography, Grid } from "@mui/material";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import FormikErrorText from "../../components/ui/FormikErrorText";
import ContentBox from "../../components/ui/ContentBox";
import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuthContext } from "../../hooks/useAuthContext";
import CourtBookingCard from "./CourtBookingCard";

// Valeurs initiales du formulaire
const initialValuesBooking = {
    duration: "",
    price: ""
};

// Schéma de validation avec Yup (messages en anglais)
const bookingValidationSchema = Yup.object().shape({
    duration: Yup.number()
        .typeError("Duration must be a number")
        .required("Duration is required"),
    price: Yup.number()
        .typeError("Price must be a number")
        .required("Price is required")
});

function CourtBooking() {
    const { user } = useAuthContext();
    const access_token = user?.access_token;

    // État pour stocker la liste des court bookings
    const [courtBookingsList, setCourtBookingsList] = useState([]);
    // État pour déclencher un rafraîchissement (par exemple après création, mise à jour ou suppression)
    const [didUpdate, setDidUpdate] = useState(false);
    const [didDelete, setDidDelete] = useState(false);

    // Fonction pour récupérer la liste des court bookings
    const fetchCourtBookings = () => {
        Api.get(Urls.GET_COURT_BOOKINGS, {
            headers: { "Content-Type": "application/json", authorization: `Bearer ${access_token}` }
        })
            .then((response) => {
                // On suppose ici que la réponse contient une propriété data.data avec la liste
                setCourtBookingsList(response?.data?.data);
            })
            .catch((error) => {
                toast.error(error?.response?.data?.message);
            });
    };

    // Récupération initiale au chargement de la page et lors d'une mise à jour
    useEffect(() => {
        if (user) {
            fetchCourtBookings();
        }
    }, [user, didUpdate, didDelete, access_token]);

    // Fonction de soumission du formulaire de création
    const onSubmitBooking = (values, { resetForm }) => {
        Api.post(
            Urls.CREATE_COURT_BOOKING,
            values,
            { headers: { "Content-Type": "application/json", authorization: `Bearer ${access_token}` } }
        )
            .then((response) => {
                toast.success("Court booking created successfully");
                resetForm();
                // Recharger la liste après la création d'un nouveau booking
                fetchCourtBookings();
                // Ou utiliser l'état didUpdate pour propager le changement aux autres composants (par exemple : setDidUpdate(!didUpdate))
            })
            .catch((error) => {
                toast.error(error?.response?.data?.message);
            });
    };

    return (
        <>
            <ToastContainer />
            <Box display="flex" flexDirection="column" gap="26px" marginTop="26px">
                <Box>
                    <Typography fontSize="22px" fontWeight="500" lineHeight="30px" color="#4B465C">
                        Court Booking
                    </Typography>
                </Box>
                {/* Formulaire de création de court booking */}
                <ContentBox>
                    <Box display="flex" flexDirection="column" gap="16px">
                        <Box>
                            <Typography fontSize="18px" fontWeight="500" lineHeight="24px" color="#4B465C">
                                Create Court Booking
                            </Typography>
                        </Box>
                        <Formik
                            initialValues={initialValuesBooking}
                            validationSchema={bookingValidationSchema}
                            onSubmit={onSubmitBooking}
                        >
                            {() => (
                                <Form>
                                    <Box sx={{ width: { xs: "100%", md: "50%" } }} marginBottom="10px">
                                        <Typography fontSize="13px" fontWeight="400" color="#4B465C" marginBottom="4px">
                                            Duration (minutes)
                                        </Typography>
                                        <Field
                                            name="duration"
                                            as={TextField}
                                            fullWidth
                                            autoComplete="off"
                                            placeholder="Enter duration"
                                        />
                                        <ErrorMessage component={FormikErrorText} name="duration" />
                                    </Box>
                                    <Box sx={{ width: { xs: "100%", md: "50%" } }} marginBottom="10px">
                                        <Typography fontSize="13px" fontWeight="400" color="#4B465C" marginBottom="4px">
                                            Price
                                        </Typography>
                                        <Field
                                            name="price"
                                            as={TextField}
                                            fullWidth
                                            autoComplete="off"
                                            placeholder="Enter price"
                                        />
                                        <ErrorMessage component={FormikErrorText} name="price" />
                                    </Box>
                                    <Box marginTop="16px" display="flex" flexDirection="row-reverse">
                                        <Button type="submit" sx={{ height: "38px" }} variant="primary_active">
                                            Create Court Booking
                                        </Button>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    </Box>
                </ContentBox>
                {/* Affichage de la liste des Court Bookings */}
                {courtBookingsList && courtBookingsList.length > 0 ? (
                    <Box>
                        <Typography
                            fontSize="20px"
                            fontWeight="500"
                            color="#4B465C"
                            marginBottom="16px"
                        >
                            Existing Court Bookings
                        </Typography>
                        <Grid container spacing={3}>
                            {courtBookingsList.map((booking) => (
                                <Grid key={booking.id} item xs={12} md={6} lg={4}>
                                    <CourtBookingCard
                                        booking={booking}
                                        access_token={access_token}
                                        didDelete={didDelete}
                                        setDidDelete={setDidDelete}
                                        didUpdate={didUpdate}
                                        setDidUpdate={setDidUpdate}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                ) : null}

            </Box>
        </>
    );
}

export default CourtBooking;
